import { Link } from "react-router-dom";
import {
  BUSINESS_NAME,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  MAX_IMAGES_PER_JOB,
  TERMS_EFFECTIVE_DATE,
} from "@makit/shared";

import { PublicLayout } from "./PublicLayout";
import { PUBLIC_PATHS } from "./paths";

/** `P-03` 이용약관. 결제사 심사와 셀러가 함께 보는 게시본이다.
 *
 * **글자의 정본은 PRD `7.0` 「로그인 전 화면의 확정 문구」 (3)이다**(2026-10-04 `design-manager`
 * 확정. 법무 `4.16.3` 게시본에서 실측에 맞춰 고친 판). 이 파일은 그 문장을 옮기기만 한다. 문장을
 * 바꿔야 하면 PRD 를 먼저 고친다.
 *
 * ⛔ **게시한 판은 그 판으로 가입한 셀러에게 약속이다**(법무 `L-X2`). 조항을 넓히거나 「~하도록
 * 하겠습니다」류로 없는 절차를 약속하지 않는다. 바꿀 때는 시행일 상수(`TERMS_EFFECTIVE_DATE`)를
 * 함께 바꾸고 제3조 2 의 고지 기간을 지킨다.
 *
 * - **제7조에 4 가 없다.** 신고된 사진과 결과를 보이지 않게 할 수단이 코드에도 사람 절차에도 없어서
 *   뺐다(2026-10-04 실측. 법무 `4.16.4`-4). 수단이 서면 약관 개정으로 넣는다.
 * - 제7조 1 은 **입력은 셀러 본인의 사진뿐**이라는 제품 원칙(PRD `2.1`)을 셀러와의 계약으로 받는 자리다.
 * - 학습 이용 동의 · 넓은 이용 허락 · 결과의 소유권 단정은 넣지 않는다(법무 `4.16.3` 머리말).
 * - 상호 · 연락처 · 장 수는 상수에서 읽는다. 사업자 정보 줄은 본문에 다시 적지 않는다. 모든 웹 화면
 *   아래 사업자 정보 블록이 이미 그린다. */
export function TermsScreen() {
  return (
    <PublicLayout title="이용약관 · makit">
      <article className="policy">
        <h1>이용약관</h1>
        <p className="meta">
          시행일 <span className="mono">{TERMS_EFFECTIVE_DATE}</span>
        </p>

        <h2>제1조 목적</h2>
        <p className="prose">
          이 약관은 {BUSINESS_NAME}(이하 「우리」)가 제공하는 makit(앱과 웹. 이하 「서비스」)을 셀러가 쓰는
          조건을 정합니다.
        </p>

        <h2>제2조 쓰는 말</h2>
        <ol>
          <li className="prose">셀러: 이 약관에 따라 서비스에 가입해 쓰는 분.</li>
          <li className="prose">
            작업: 결제와 만들기의 단위. 한 작업에 사진을 최대 {MAX_IMAGES_PER_JOB}장까지 담습니다.
          </li>
          <li className="prose">장: 작업에 담은 사진 한 장과 그 사진으로 만든 결과.</li>
          <li className="prose">결과: 우리가 셀러의 사진으로 만들어 드리는 배경 지운 사진과 상세페이지 이미지.</li>
        </ol>

        <h2>제3조 약관의 게시와 변경</h2>
        <ol>
          <li className="prose">우리는 이 약관을 웹 화면 맨 아래의 링크로 게시합니다.</li>
          <li className="prose">
            약관을 바꾸면 시행 7일 전에, 셀러에게 불리하게 바꾸면 시행 30일 전에 앱과 웹에 알립니다.
          </li>
          <li className="prose">바뀐 약관에 동의하지 않으시면 시행 전에 탈퇴하실 수 있습니다.</li>
        </ol>

        <h2>제4조 가입과 계정</h2>
        <ol>
          <li className="prose">셀러는 네이버 또는 카카오 계정으로 가입 · 로그인합니다.</li>
          <li className="prose">
            계정은 가입하신 분이 관리합니다. 다른 사람이 내 계정을 쓰고 있다고 생각되면 바로 제14조의 연락처로
            알려 주세요.
          </li>
        </ol>

        <h2>제5조 서비스 내용</h2>
        <ol>
          <li className="prose">
            우리는 셀러가 올린 상품 사진의 배경을 지우고 상세페이지에 쓸 이미지로 만들어 드립니다.
          </li>
          <li className="prose">결과는 화면에서 확인하고 파일로 받습니다. 배송하는 물건은 없습니다.</li>
          <li className="prose">
            결과가 나오는 데 걸리는 시간, 결과를 받을 수 있는 기간, 올리신 사진을 지우는 때는 <Link to={PUBLIC_PATHS.service}>서비스 안내</Link>와{" "}
            <Link to={PUBLIC_PATHS.privacy}>개인정보처리방침</Link>에 적힌 대로입니다.
          </li>
        </ol>

        <h2>제6조 요금 · 결제 · 취소</h2>
        <p className="prose">
          금액, 결제 방법, 결제 취소와 돌아가는 금액은 서비스 안내와 <Link to={PUBLIC_PATHS.refund}>환불정책</Link>에
          적힌 대로입니다.
        </p>

        <h2>제7조 올리는 사진</h2>
        <ol>
          <li className="prose">
            셀러는 <b>직접 찍었거나 쓸 권리를 가진 사진만</b> 올립니다. 다른 판매자나 다른 사이트의 상품 사진,
            남이 찍은 사진을 그 권리자의 허락 없이 올리지 않습니다.
          </li>
          <li className="prose">사진에 사람이 찍혀 있으면 그 사람의 동의를 받은 사진만 올립니다.</li>
          <li className="prose">
            1과 2를 지키지 않은 사진 때문에 다른 사람과 다툼이 생기면 셀러가 그 책임을 집니다. 다만 우리의
            잘못으로 생긴 부분은 그렇지 않습니다.
          </li>
        </ol>

        <h2>제8조 우리가 사진을 쓰는 범위</h2>
        <ol>
          <li className="prose">
            우리는 셀러가 올린 사진을 그 셀러의 결과를 만들고, 보여 드리고, 보관하는 데에만 씁니다.
          </li>
          <li className="prose">
            결과를 만들기 위해 처리를 맡기는 곳에 사진을 보냅니다. 받는 곳은 개인정보처리방침 제5조 · 제6조에
            있습니다.
          </li>
          <li className="prose">
            우리는 지금 셀러의 사진을 서비스 개선이나 다른 목적에 쓰지 않습니다. 쓰려면 이 약관과
            개인정보처리방침을 먼저 바꿔 알리고, 직접 켜신 셀러의 사진만 씁니다.
          </li>
        </ol>

        <h2>제9조 결과의 사용</h2>
        <ol>
          <li className="prose">셀러는 결과를 자기 상품의 판매에 자유롭게 쓸 수 있습니다.</li>
          <li className="prose">결과에 들어간 원래 사진의 권리는 그 사진의 권리자에게 그대로 있습니다.</li>
          <li className="prose">
            결과를 어디에 어떻게 게시할지는 셀러가 정하고, 게시한 내용(상품 설명 · 표시 등)의 책임은 셀러에게
            있습니다.
          </li>
        </ol>

        <h2>제10조 이용 제한</h2>
        <ol>
          <li className="prose">
            제7조를 지키지 않거나, 서비스를 정상적이지 않은 방법으로 쓰거나, 다른 셀러의 정보에 접근하려 하면
            우리는 서비스 이용을 제한할 수 있습니다.
          </li>
          <li className="prose">
            제한하기 전에 이유를 알립니다. 다만 다른 사람의 권리나 서비스 운영을 지키기 위해 급한 경우에는 먼저
            제한하고 바로 알립니다.
          </li>
          <li className="prose">셀러는 제한에 대해 제14조의 연락처로 의견을 내실 수 있습니다.</li>
        </ol>

        <h2>제11조 탈퇴</h2>
        <ol>
          <li className="prose">
            셀러는 언제든 앱의 마이페이지에서 탈퇴하실 수 있습니다. 웹만 쓰시면 개인정보처리방침 제10조의 창구로
            탈퇴를 요청하실 수 있습니다.
          </li>
          <li className="prose">탈퇴하면 무엇을 지우고 무엇이 남는지는 개인정보처리방침 제3조에 있습니다.</li>
          <li className="prose">
            탈퇴 전에 결제한 작업의 결제 취소와 돌아갈 금액은 탈퇴한 뒤에도 환불정책에 따릅니다.
          </li>
        </ol>

        <h2>제12조 서비스의 변경과 중단</h2>
        <ol>
          <li className="prose">
            서비스 내용을 바꾸거나 멈춰야 할 때는 미리 앱과 웹에 알립니다. 장애처럼 미리 알릴 수 없는 경우에는
            알게 된 뒤 바로 알립니다.
          </li>
          <li className="prose">결제했는데 서비스가 멈춰 만들지 못한 장은 환불정책의 못 만든 장과 같게 다룹니다.</li>
        </ol>

        <h2>제13조 책임</h2>
        <ol>
          <li className="prose">우리의 고의나 과실로 셀러에게 손해가 생기면 우리가 배상합니다.</li>
          <li className="prose">셀러의 고의나 과실로 우리나 다른 사람에게 손해가 생기면 셀러가 배상합니다.</li>
        </ol>

        <h2>제14조 문의와 다툼</h2>
        <ol>
          <li className="prose">
            서비스에 관한 문의와 알려 주실 것은 이메일 <span className="mono">{CONTACT_EMAIL}</span> 또는 전화{" "}
            <span className="mono">{CONTACT_PHONE}</span>(이하 「연락처」)으로 받습니다.
          </li>
          <li className="prose">
            이 약관과 서비스에 관한 다툼은 대한민국 법을 따르고, 관할 법원은 민사소송법이 정하는 곳으로 합니다.
          </li>
        </ol>
      </article>
    </PublicLayout>
  );
}
